// GAVION X3: data quality on Supabase. Every RPC re-checks is_admin() server side (P0001 'forbidden').
import type { DqCompany, FixKind, MergePicks } from "../../domain/dataQuality";
import {
  DqApiError,
  type DataQualityClient,
  type DqSummary,
  type DuplicatePair,
  type SuspectEmail,
} from "./types";

type RpcError = { code?: string; message?: string; details?: string } | null;
export type DqRpcClient = {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: RpcError }>;
};

const CODE = /^[a-z_]{3,40}$/;
/** P0001 messages are stable codes; 42501 (no EXECUTE) is forbidden; anything else is unknown. */
export const dqErrorCode = (e: RpcError): string => {
  if (!e) return "unknown";
  if (e.code === "42501") return "forbidden";
  return e.code === "P0001" && CODE.test(e.message ?? "")
    ? (e.message as string)
    : "unknown";
};

const obj = (v: unknown): Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};

export const createSupabaseDataQualityClient = (
  getClient: () => DqRpcClient,
): DataQualityClient => {
  const call = async (fn: string, args?: Record<string, unknown>) => {
    const { data, error } = await getClient().rpc(fn, args);
    if (error) throw new DqApiError(dqErrorCode(error), error.details);
    return data;
  };
  return {
    async duplicateCount() {
      return Number((await call("data_quality_duplicate_count")) ?? 0);
    },
    async summary() {
      return obj(await call("data_quality_summary")) as DqSummary;
    },
    async duplicates(limit = 100) {
      const r = obj(await call("duplicate_candidates", { p_limit: limit }));
      return {
        total: Number(r.total ?? 0),
        pairs: (Array.isArray(r.pairs) ? r.pairs : []) as DuplicatePair[],
      };
    },
    async merge(survivorId: number, loserId: number, picks: MergePicks) {
      const r = obj(
        await call("merge_companies", {
          p_survivor_id: survivorId,
          p_loser_id: loserId,
          p_picks: picks,
        }),
      );
      return {
        survivorId: Number(r.survivor_id),
        loserId: Number(r.loser_id),
        moved: obj(r.moved) as Record<string, number>,
      };
    },
    async dismiss(a: number, b: number) {
      await call("dismiss_duplicate", { p_company_a: a, p_company_b: b });
    },
    async queue(kind: FixKind, limit = 25, offset = 0) {
      const r = obj(
        await call("data_quality_queue", {
          p_kind: kind,
          p_limit: limit,
          p_offset: offset,
        }),
      );
      return {
        total: Number(r.total ?? 0),
        rows: (Array.isArray(r.rows) ? r.rows : []) as DqCompany[],
      };
    },
    async setPhone(id: number, phone: string) {
      const r = obj(
        await call("dq_set_phone", { p_company_id: id, p_phone: phone }),
      );
      return {
        phoneE164: String(r.phone_e164),
        doNotCall: r.do_not_call === true,
      };
    },
    async markBadNumber(id: number) {
      await call("dq_mark_bad_number", { p_company_id: id });
    },
    // GAVION suspect emails (89_)
    async attentionCount() {
      return Number((await call("data_quality_attention_count")) ?? 0);
    },
    async suspectEmails(limit = 100) {
      const r = obj(
        await call("data_quality_suspect_emails", { p_limit: limit }),
      );
      return {
        total: Number(r.total ?? 0),
        rows: (Array.isArray(r.rows) ? r.rows : []) as SuspectEmail[],
      };
    },
    async setEmail(id: number, email: string) {
      const r = obj(
        await call("dq_set_email", { p_company_id: id, p_email: email }),
      );
      return { email: String(r.email) };
    },
    async dismissSuspectEmail(id: number) {
      await call("dq_dismiss_suspect_email", { p_company_id: id });
    },
  };
};
